import { useParams, useNavigate } from "@/lib/router-compat";
import { useEffect } from "react";
import { Link } from "@/lib/router-compat";
import {
  CheckCircle, ArrowRight, MapPin, Zap, Wrench, Clock, ShieldCheck, AlertTriangle, Phone,
  Award, Building2, Hammer, AlertOctagon, Factory, CalendarCheck, ClipboardList, Thermometer,
  BarChart3, FileText, HeartPulse, Snowflake, Flame, Wind, Gauge, PlusCircle, CalendarClock,
  ThermometerSun, Shield, Lightbulb, Layout as LayoutIcon, Eye, Ruler, Palette, TrendingDown, Sun,
  RefreshCw, Leaf, DollarSign, Timer, Receipt, Search, Handshake, PiggyBank, CheckCircle2,
  TrendingUp, LineChart, BadgeCheck, Landmark, Maximize2, Trophy, Cpu, Lock, Wifi, Monitor,
  Settings, Map, Navigation, GraduationCap, Store, Church, Hotel, Heart, Target, Calendar,
  HelpCircle, Briefcase, Users, Star, MessageSquare, ThumbsUp, Quote, UserCircle, Mail, BookOpen, Tag,
  type LucideIcon
} from "lucide-react";
import Layout from "@/components/Layout";
import PageSEO from "@/components/PageSEO";
import PageHero, { HeroConfig } from "@/components/PageHero";
import TestimonialRow from "@/components/TestimonialRow";
import AtAGlance from "@/components/AtAGlance";
import FactStrip, { Fact, defaultFacts } from "@/components/FactStrip";
import { useMainHeadings, PageJumpNav, PageJumpSelect } from "@/components/PageJumpNav";
import RailContactCard from "@/components/RailContactCard";
import ContactForm from "@/components/ContactForm";
import CalloutBox from "@/components/CalloutBox";
import { breadcrumbSchema, faqSchema, serviceSchema } from "@/lib/seo";
import { ServiceData, allElectricalServices, hvacServices, energyServices, specialtyServices, stadiumServices, industryServices } from "@/data/services";
import { blogPosts } from "@/data/blog-posts";
import { clusterSpokes } from "@/data/cluster-links";
import { blogImages } from "@/assets/blog";

import { heroPhotos, heroPhotoAlts, heroPhotoFallback, heroPhotoFallbackAlts } from "@/lib/heroImages";

// ═══════════ HERO CONFIGS PER SLUG ═══════════

interface ServiceVisuals {
  hero: HeroConfig;
  featureCards?: { icon: LucideIcon; title: string; description: string }[];
  stats?: { icon: LucideIcon; value: string; label: string }[];
  callout?: { icon: LucideIcon; title: string; description: string };
}

const serviceVisuals: Record<string, ServiceVisuals> = {
  // ═══ ELECTRICAL SERVICES ═══
  "commercial-electrical-contractor": {
    hero: { pattern: "circuit", watermarkIcon: Zap, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Building2, title: "Commercial Expertise", description: "Office build-outs, retail, restaurants, and large-scale construction." },
      { icon: ShieldCheck, title: "Licensed & Insured", description: "KY License CE64623. Fully insured with Gold Standard safety." },
      { icon: Award, title: "1-Year Warranty", description: "All work backed by our comprehensive warranty program." },
    ],
    stats: [
      { icon: Calendar, value: "2013", label: "Founded" },
      { icon: Users, value: "100+", label: "Years Combined Exp." },
      { icon: Shield, value: "Zero", label: "Safety Incidents" },
      { icon: Map, value: "KY/IN", label: "Coverage Area" },
    ],
  },
  "industrial-electrical-contractor": {
    hero: { pattern: "circuit", watermarkIcon: Factory, watermarkPosition: "bottom-left", textAlign: "left" },
    featureCards: [
      { icon: Factory, title: "Industrial Facilities", description: "Manufacturing, warehousing, distribution, and heavy industry." },
      { icon: Zap, title: "High-Amperage Systems", description: "3-phase power, MCC installations, and VFD programming." },
      { icon: Shield, title: "Safety-First", description: "Gold Standard safety award. OSHA and NEC compliant." },
    ],
  },
  "electrical-service-and-repair": {
    hero: { pattern: "diagonal", watermarkIcon: Wrench, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Clock, title: "Same-Day Service", description: "Fast dispatch to your facility, often within hours." },
      { icon: ShieldCheck, title: "Licensed Electricians", description: "All technicians are licensed and background-checked." },
      { icon: AlertTriangle, title: "Emergency Available", description: "24/7 emergency electrical service for critical issues." },
    ],
    callout: { icon: Phone, title: "Need Emergency Service?", description: "Call (502) 921-3777 for immediate same-day dispatch. We understand downtime costs money." },
  },
  "electrical-contracting": {
    hero: { pattern: "grid", watermarkIcon: Building2, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Building2, title: "New Construction", description: "Complete electrical systems for new commercial buildings." },
      { icon: Hammer, title: "Remodels & Renovations", description: "Upgrading existing spaces with modern electrical infrastructure." },
      { icon: AlertOctagon, title: "Hazardous Locations", description: "Specialized wiring for classified hazardous environments." },
    ],
  },
  "preventative-maintenance": {
    hero: { pattern: "dots", watermarkIcon: CalendarCheck, watermarkPosition: "bottom-left", textAlign: "left" },
    featureCards: [
      { icon: CalendarCheck, title: "Scheduled Maintenance", description: "Regular inspections to catch issues before they become emergencies." },
      { icon: ClipboardList, title: "Detailed Reports", description: "Comprehensive inspection reports with recommendations." },
      { icon: HeartPulse, title: "Equipment Monitoring", description: "Track equipment health and predict maintenance needs." },
    ],
  },
  "electrical-panel-upgrades": {
    hero: { pattern: "circuit", watermarkIcon: Zap, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Zap, title: "Panel Upgrades", description: "Modern panels to handle increased power demands." },
      { icon: ShieldCheck, title: "Code Compliant", description: "All upgrades meet current NEC and local codes." },
      { icon: Award, title: "Quality Materials", description: "We use top-tier panels from trusted manufacturers." },
    ],
  },
  "generator-installation": {
    hero: { pattern: "diagonal", watermarkIcon: Zap, watermarkPosition: "bottom-right", textAlign: "left" },
    featureCards: [
      { icon: Zap, title: "Backup Power", description: "Keep your facility running during grid outages." },
      { icon: Settings, title: "Automatic Transfer", description: "Seamless switchover when utility power fails." },
      { icon: Wrench, title: "Maintenance Plans", description: "Regular generator testing and maintenance programs." },
    ],
  },
  "motor-control-centers": {
    hero: { pattern: "circuit", watermarkIcon: Cpu, watermarkPosition: "center-right", textAlign: "left" },
    featureCards: [
      { icon: Cpu, title: "MCC Installation", description: "Eaton, Allen-Bradley, and all major MCC brands." },
      { icon: Gauge, title: "Motor Controls", description: "VFDs, soft starters, and motor protection systems." },
      { icon: Shield, title: "Arc Flash Safety", description: "Arc flash studies, labeling, and PPE coordination." },
    ],
  },
  "plc-motor-controls": {
    hero: { pattern: "circuit", watermarkIcon: Cpu, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Cpu, title: "PLC Programming", description: "Custom logic for automated production systems." },
      { icon: Gauge, title: "VFD Installation", description: "Variable frequency drives for motor speed control." },
      { icon: Monitor, title: "HMI Interfaces", description: "Touch-screen operator interfaces and SCADA." },
    ],
  },
  "fire-alarm-systems": {
    hero: { pattern: "dots", watermarkIcon: AlertTriangle, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: AlertTriangle, title: "Fire Detection", description: "Smoke, heat, and flame detection systems." },
      { icon: Shield, title: "Code Compliant", description: "NFPA 72 compliant design and installation." },
      { icon: ClipboardList, title: "Inspection & Testing", description: "Annual inspections and system testing." },
    ],
  },
  "emergency-electrical-service": {
    hero: { pattern: "diagonal", watermarkIcon: AlertTriangle, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Clock, title: "Rapid Response", description: "Same-day emergency dispatch to your facility." },
      { icon: Zap, title: "Power Restoration", description: "Get your operations back online quickly and safely." },
      { icon: Phone, title: "24/7 Available", description: "Call anytime — we're here when you need us." },
    ],
    callout: { icon: Phone, title: "Electrical Emergency? Call Now.", description: "(502) 921-3777 — Same-day dispatch for commercial and industrial emergencies." },
  },
  "electrical-safety-inspections": {
    hero: { pattern: "grid", watermarkIcon: ClipboardList, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: ClipboardList, title: "Safety Audits", description: "Comprehensive electrical safety inspections." },
      { icon: ShieldCheck, title: "Code Compliance", description: "Identify and resolve code violations." },
      { icon: FileText, title: "Detailed Reports", description: "Written reports with prioritized recommendations." },
    ],
  },
  "data-cabling-low-voltage": {
    hero: { pattern: "circuit", watermarkIcon: Wifi, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Wifi, title: "Structured Cabling", description: "Cat6, Cat6A, and fiber optic installations." },
      { icon: Monitor, title: "Network Infrastructure", description: "Server rooms, patch panels, and cable management." },
      { icon: Lock, title: "Security Systems", description: "CCTV, access control, and intercom wiring." },
    ],
  },
  "electrical-design-build": {
    hero: { pattern: "grid", watermarkIcon: LayoutIcon, watermarkPosition: "bottom-left", textAlign: "left" },
    featureCards: [
      { icon: LayoutIcon, title: "Design Services", description: "Complete electrical design from concept to completion." },
      { icon: Building2, title: "New Construction", description: "Ground-up electrical for commercial and industrial." },
      { icon: Hammer, title: "Build & Install", description: "Single-source responsibility for design and construction." },
    ],
  },
  "switchgear-installation": {
    hero: { pattern: "circuit", watermarkIcon: Zap, watermarkPosition: "center-right", textAlign: "left" },
    featureCards: [
      { icon: Zap, title: "Switchgear Install", description: "Medium and low voltage switchgear installation." },
      { icon: Shield, title: "Arc Flash Studies", description: "Safety analysis and protective coordination." },
      { icon: Wrench, title: "Maintenance", description: "Preventive testing and maintenance programs." },
    ],
  },
  "high-voltage-electrical": {
    hero: { pattern: "diagonal", watermarkIcon: Zap, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Zap, title: "High Voltage", description: "Medium and high voltage system design and install." },
      { icon: Shield, title: "Safety Protocols", description: "Strict OSHA and NFPA 70E compliance." },
      { icon: ClipboardList, title: "Testing", description: "Comprehensive commissioning and acceptance testing." },
    ],
  },
  "transformer-installation": {
    hero: { pattern: "circuit", watermarkIcon: Zap, watermarkPosition: "bottom-right", textAlign: "left" },
    featureCards: [
      { icon: Zap, title: "Transformer Install", description: "Pad-mount, pole-mount, and indoor transformers." },
      { icon: Gauge, title: "Sizing & Design", description: "Proper transformer sizing for your load requirements." },
      { icon: Wrench, title: "Testing & Commissioning", description: "Complete testing before energization." },
    ],
  },

  // ═══ HVAC SERVICES ═══
  "commercial-hvac": {
    hero: { pattern: "snowflake", watermarkIcon: Snowflake, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Snowflake, title: "Cooling Systems", description: "Commercial AC, chillers, and cooling towers." },
      { icon: Flame, title: "Heating Systems", description: "Boilers, furnaces, and radiant heating." },
      { icon: Wind, title: "Ventilation", description: "Make-up air units, exhaust, and air handling." },
    ],
  },
  "industrial-hvac": {
    hero: { pattern: "waves", watermarkIcon: Factory, watermarkPosition: "bottom-left", textAlign: "left" },
    featureCards: [
      { icon: Factory, title: "Industrial Scale", description: "Large-scale HVAC for manufacturing and warehousing." },
      { icon: Wind, title: "Process Ventilation", description: "Exhaust and ventilation for industrial environments." },
      { icon: Thermometer, title: "Climate Control", description: "Precise temperature and humidity control." },
    ],
  },
  "residential-hvac": {
    hero: { pattern: "waves", watermarkIcon: Thermometer, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Thermometer, title: "Home Comfort", description: "Heating and cooling for homes and condos." },
      { icon: Shield, title: "Licensed HM06538", description: "Kentucky HVAC Master license for heating and cooling work." },
      { icon: DollarSign, title: "Free Estimates", description: "No-obligation quotes for residential HVAC." },
    ],
  },
  "hvac-installation": {
    hero: { pattern: "waves", watermarkIcon: PlusCircle, watermarkPosition: "bottom-right", textAlign: "left" },
    featureCards: [
      { icon: Snowflake, title: "Full Installation", description: "New HVAC systems sized and installed correctly." },
      { icon: Wind, title: "Ductwork", description: "Custom ductwork design and fabrication." },
      { icon: Shield, title: "Warranty", description: "Manufacturer warranties plus our 1-year labor warranty." },
    ],
  },
  "hvac-service-repair": {
    hero: { pattern: "snowflake", watermarkIcon: Wrench, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Wrench, title: "Expert Repair", description: "Troubleshooting and repair of all HVAC brands." },
      { icon: Clock, title: "Fast Response", description: "Quick dispatch to minimize downtime." },
      { icon: ShieldCheck, title: "Licensed Techs", description: "All technicians are licensed and certified." },
    ],
  },
  "hvac-preventative-maintenance": {
    hero: { pattern: "dots", watermarkIcon: CalendarCheck, watermarkPosition: "bottom-left", textAlign: "left" },
    featureCards: [
      { icon: CalendarCheck, title: "Scheduled Service", description: "Regular maintenance prevents costly breakdowns." },
      { icon: ThermometerSun, title: "Seasonal Prep", description: "Spring and fall tune-ups for peak performance." },
      { icon: FileText, title: "Service Reports", description: "Detailed reports after every maintenance visit." },
    ],
  },
  "building-automation-systems": {
    hero: { pattern: "circuit", watermarkIcon: Cpu, watermarkPosition: "center-right", textAlign: "left" },
    featureCards: [
      { icon: Cpu, title: "BAS Integration", description: "Unified control of HVAC, lighting, and access." },
      { icon: Monitor, title: "Single Platform", description: "Distech, EcoStruxure, and open protocols." },
      { icon: Lock, title: "No Lock-in", description: "No proprietary equipment or long-term contracts." },
    ],
    callout: { icon: Shield, title: "No Long-Term Contracts Required", description: "We don't lock you into proprietary systems. Our BAS solutions use open protocols so you're never trapped." },
  },
  "temperature-controls": {
    hero: { pattern: "snowflake", watermarkIcon: Thermometer, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Thermometer, title: "Precision Control", description: "Accurate temperature management for any space." },
      { icon: Cpu, title: "Smart Controls", description: "Programmable thermostats and zone control." },
      { icon: TrendingDown, title: "Energy Savings", description: "Optimized setpoints reduce utility costs." },
    ],
  },
  "ductwork-installation": {
    hero: { pattern: "waves", watermarkIcon: Wind, watermarkPosition: "bottom-right", textAlign: "left" },
    featureCards: [
      { icon: Wind, title: "Custom Ductwork", description: "Fabricated and installed to your specifications." },
      { icon: Ruler, title: "Engineered Design", description: "Proper sizing for balanced airflow." },
      { icon: Shield, title: "Sealed & Tested", description: "Leak-tested for maximum efficiency." },
    ],
  },
  "heat-pump-installation": {
    hero: { pattern: "snowflake", watermarkIcon: RefreshCw, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: RefreshCw, title: "Dual Function", description: "Heat pumps provide both heating and cooling." },
      { icon: Leaf, title: "Energy Efficient", description: "Up to 300% more efficient than traditional systems." },
      { icon: DollarSign, title: "Tax Credits", description: "Eligible for federal energy efficiency tax credits." },
    ],
  },
  "furnace-installation-repair": {
    hero: { pattern: "waves", watermarkIcon: Flame, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Flame, title: "Furnace Systems", description: "Gas, electric, and oil furnace installation." },
      { icon: Wrench, title: "Repair Service", description: "Troubleshooting and repair of all furnace brands." },
      { icon: Shield, title: "Safety First", description: "Carbon monoxide testing with every service call." },
    ],
  },
  "ac-installation-repair": {
    hero: { pattern: "snowflake", watermarkIcon: Snowflake, watermarkPosition: "bottom-right", textAlign: "left" },
    featureCards: [
      { icon: Snowflake, title: "AC Installation", description: "Split systems, packaged units, and ductless mini-splits." },
      { icon: Wrench, title: "AC Repair", description: "Same-day repair service for all makes and models." },
      { icon: Gauge, title: "Performance", description: "Refrigerant charge and airflow optimization." },
    ],
  },
  "geothermal-heating-cooling": {
    hero: { pattern: "waves", watermarkIcon: Leaf, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Leaf, title: "Ground-Source Energy", description: "Harness stable underground temperatures for 300–600% efficiency." },
      { icon: RefreshCw, title: "Heat & Cool", description: "One system provides both heating and cooling year-round." },
      { icon: DollarSign, title: "30% Tax Credit", description: "Federal ITC covers 30% of total installation costs through 2032." },
    ],
    stats: [
      { icon: TrendingDown, value: "50%", label: "Lower Utility Bills" },
      { icon: Timer, value: "25+", label: "Year System Life" },
      { icon: Leaf, value: "Zero", label: "On-Site Emissions" },
      { icon: DollarSign, value: "30%", label: "Federal Tax Credit" },
    ],
    callout: { icon: Leaf, title: "Is Geothermal Right for Your Property?", description: "Louisville's geology and climate make it ideal for geothermal. Call (502) 921-3777 for a free site assessment." },
  },
  "indoor-air-quality": {
    hero: { pattern: "dots", watermarkIcon: Wind, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Wind, title: "Air Purification", description: "HEPA filtration, UV-C germicidal systems, and bipolar ionization." },
      { icon: Gauge, title: "IAQ Testing", description: "Comprehensive indoor air quality assessments with detailed reports." },
      { icon: Shield, title: "Healthier Spaces", description: "Reduce allergens, VOCs, mold spores, and airborne pathogens." },
    ],
    stats: [
      { icon: Wind, value: "99.97%", label: "HEPA Particle Capture" },
      { icon: Shield, value: "5x", label: "Indoor vs Outdoor Pollution" },
      { icon: TrendingDown, value: "40%", label: "Fewer Sick Days" },
      { icon: Leaf, value: "24/7", label: "Continuous Purification" },
    ],
    callout: { icon: Wind, title: "Concerned About Your Indoor Air?", description: "Louisville ranks in the top 20 worst cities for allergies. Call (502) 921-3777 for a free IAQ assessment." },
  },
  "commercial-refrigeration": {
    hero: { pattern: "snowflake", watermarkIcon: Snowflake, watermarkPosition: "center-right", textAlign: "left" },
    featureCards: [
      { icon: Snowflake, title: "Walk-in Coolers", description: "Installation and repair of walk-in units." },
      { icon: Thermometer, title: "Temperature Monitoring", description: "Alarms and monitoring for food safety compliance." },
      { icon: Wrench, title: "Service Plans", description: "Preventive maintenance for commercial refrigeration." },
    ],
  },

  // ═══ ENERGY MANAGEMENT ═══
  "lighting-design": {
    hero: { pattern: "spotlight", watermarkIcon: Lightbulb, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Lightbulb, title: "Custom Design", description: "Lighting layouts tailored to your space and needs." },
      { icon: Eye, title: "Light Analysis", description: "Photometric studies for optimal illumination." },
      { icon: TrendingDown, title: "Energy Reduction", description: "Efficient designs that lower utility costs." },
    ],
  },
  "lighting-retrofits": {
    hero: { pattern: "bars", watermarkIcon: RefreshCw, watermarkPosition: "bottom-right", textAlign: "center" },
    featureCards: [
      { icon: RefreshCw, title: "Retrofit Solutions", description: "Upgrade existing fixtures to high-efficiency LED." },
      { icon: DollarSign, title: "Utility Rebates", description: "We help you maximize available rebate programs." },
      { icon: Timer, title: "Less Maintenance", description: "LED fixtures last 50,000+ hours." },
    ],
    stats: [
      { icon: TrendingDown, value: "70%", label: "Energy Savings" },
      { icon: Timer, value: "50K+", label: "Hour LED Life" },
      { icon: DollarSign, value: "<2yr", label: "Typical Payback" },
      { icon: Leaf, value: "Zero", label: "Mercury Content" },
    ],
  },
  "led-lighting-upgrades": {
    hero: { pattern: "spotlight", watermarkIcon: Lightbulb, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Lightbulb, title: "LED Upgrades", description: "Replace fluorescent, HID, and incandescent with LED." },
      { icon: Eye, title: "Better Visibility", description: "Improved light quality and color rendering." },
      { icon: DollarSign, title: "Cost Savings", description: "Dramatically lower energy and maintenance costs." },
    ],
  },
  "energy-audits": {
    hero: { pattern: "bars", watermarkIcon: ClipboardList, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: ClipboardList, title: "Certified Audits", description: "Recognized by Energy Audit Institute." },
      { icon: Search, title: "Facility Analysis", description: "Comprehensive review of energy consumption." },
      { icon: BarChart3, title: "Data-Driven", description: "Detailed reports with actionable recommendations." },
    ],
  },
  "energy-bill-analysis": {
    hero: { pattern: "bars", watermarkIcon: Receipt, watermarkPosition: "bottom-right", textAlign: "left" },
    featureCards: [
      { icon: Receipt, title: "Bill Review", description: "We analyze your utility bills for overcharges." },
      { icon: Search, title: "Find Savings", description: "Identify billing errors and rate optimization." },
      { icon: DollarSign, title: "No Risk", description: "Free analysis. No savings found = no fee." },
    ],
    callout: { icon: Shield, title: "Free Initial Analysis — No Savings, No Fee", description: "We're one of the very few companies offering this service. If we don't find savings, you pay nothing." },
  },
  "guaranteed-energy-savings": {
    hero: { pattern: "bars", watermarkIcon: Shield, watermarkPosition: "center-right", textAlign: "left" },
    featureCards: [
      { icon: Shield, title: "Guaranteed Savings", description: "Performance-based contracts with guaranteed ROI." },
      { icon: DollarSign, title: "No Upfront Cost", description: "Savings pay for the improvements over time." },
      { icon: LineChart, title: "M&V Tracking", description: "Measurement & verification of actual savings." },
    ],
  },
  "energy-efficiency-consulting": {
    hero: { pattern: "bars", watermarkIcon: Lightbulb, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Lightbulb, title: "Expert Consulting", description: "Certified energy auditors assess your facility." },
      { icon: TrendingDown, title: "Reduce Costs", description: "Strategies to lower energy consumption." },
      { icon: Leaf, title: "Sustainability", description: "Green initiatives that improve your bottom line." },
    ],
  },

  // ═══ STADIUM & SPORTS LIGHTING ═══
  "stadium-lighting-design": {
    hero: { pattern: "spotlight", watermarkIcon: Sun, watermarkPosition: "center-right", textAlign: "center" },
    featureCards: [
      { icon: Sun, title: "Stadium Lighting", description: "Professional-grade LED lighting for sports venues." },
      { icon: Maximize2, title: "Full Coverage", description: "Uniform illumination across entire playing surfaces." },
      { icon: Trophy, title: "Competition Ready", description: "Meets NCAA, FIFA, and broadcast lighting standards." },
    ],
  },
  "football-stadium-lighting": {
    hero: { pattern: "spotlight", watermarkIcon: Trophy, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Sun, title: "Field Lighting", description: "High-output LED floodlights for football fields." },
      { icon: Eye, title: "Broadcast Quality", description: "Lighting levels for HD video and live streaming." },
      { icon: DollarSign, title: "Energy Savings", description: "LED uses 60-75% less energy than metal halide." },
    ],
  },
  "baseball-field-lighting": {
    hero: { pattern: "spotlight", watermarkIcon: Sun, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Sun, title: "Diamond Lighting", description: "Infield and outfield lighting design." },
      { icon: Ruler, title: "Custom Layout", description: "Pole placement optimized for your field dimensions." },
      { icon: Shield, title: "Glare Control", description: "Precision optics to minimize glare and spill." },
    ],
  },
  "basketball-arena-lighting": {
    hero: { pattern: "spotlight", watermarkIcon: Trophy, watermarkPosition: "bottom-right", textAlign: "left" },
    featureCards: [
      { icon: Sun, title: "Arena Lighting", description: "Indoor LED lighting for gyms and arenas." },
      { icon: Eye, title: "Glare-Free", description: "No harsh shadows or hotspots on the court." },
      { icon: RefreshCw, title: "Retrofit Options", description: "Upgrade existing gym lighting to LED." },
    ],
  },
  "soccer-field-lighting": {
    hero: { pattern: "spotlight", watermarkIcon: Sun, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Sun, title: "Pitch Lighting", description: "Uniform LED illumination for soccer fields." },
      { icon: Maximize2, title: "Full Coverage", description: "Edge-to-edge coverage for regulation play." },
      { icon: Leaf, title: "Dark Sky", description: "Light pollution control for community fields." },
    ],
  },
  "tennis-court-lighting": {
    hero: { pattern: "spotlight", watermarkIcon: Sun, watermarkPosition: "bottom-left", textAlign: "left" },
    featureCards: [
      { icon: Sun, title: "Court Lighting", description: "LED lighting for indoor and outdoor tennis." },
      { icon: Eye, title: "Player Comfort", description: "Minimal glare for clear ball visibility." },
      { icon: DollarSign, title: "Affordable", description: "Cost-effective solutions for clubs and parks." },
    ],
  },
  "track-field-lighting": {
    hero: { pattern: "spotlight", watermarkIcon: Sun, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Sun, title: "Track Lighting", description: "Complete illumination for track and field events." },
      { icon: Ruler, title: "Even Coverage", description: "Uniform light levels across all event areas." },
      { icon: Trophy, title: "Meet Standards", description: "Lighting that meets competition requirements." },
    ],
  },
  "led-stadium-retrofits": {
    hero: { pattern: "spotlight", watermarkIcon: RefreshCw, watermarkPosition: "center-right", textAlign: "left" },
    featureCards: [
      { icon: RefreshCw, title: "LED Retrofit", description: "Replace metal halide with instant-on LED." },
      { icon: TrendingDown, title: "Lower Costs", description: "Reduce energy and maintenance expenses." },
      { icon: Sun, title: "Better Light", description: "Superior color rendering and brightness." },
    ],
  },
  "high-school-athletic-lighting": {
    hero: { pattern: "spotlight", watermarkIcon: Trophy, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Trophy, title: "Athletic Lighting", description: "Multi-sport lighting for high school facilities." },
      { icon: DollarSign, title: "Budget-Friendly", description: "Financing options for school districts." },
      { icon: Shield, title: "Safety", description: "Proper lighting for safe play and spectating." },
    ],
  },
  "recreational-park-lighting": {
    hero: { pattern: "spotlight", watermarkIcon: Sun, watermarkPosition: "bottom-right", textAlign: "left" },
    featureCards: [
      { icon: Sun, title: "Park Lighting", description: "Community park and recreation field lighting." },
      { icon: Leaf, title: "Dark Sky", description: "Environmentally responsible lighting design." },
      { icon: Clock, title: "Controls", description: "Timers and smart controls for efficient operation." },
    ],
  },

  // ═══ INDUSTRY PAGES ═══
  "manufacturing-electrical": {
    hero: { pattern: "circuit", watermarkIcon: Factory, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Factory, title: "Manufacturing", description: "Electrical systems for production environments." },
      { icon: Zap, title: "Power Systems", description: "3-phase distribution and motor controls." },
      { icon: Shield, title: "Safety Compliance", description: "NEC, OSHA, and industry-specific standards." },
    ],
  },
  "automotive-plant-electrical": {
    hero: { pattern: "circuit", watermarkIcon: Factory, watermarkPosition: "bottom-left", textAlign: "left" },
    featureCards: [
      { icon: Factory, title: "Automotive Plants", description: "Production line electrical and controls." },
      { icon: Cpu, title: "Automation", description: "PLC, robotics, and conveyor wiring." },
      { icon: Clock, title: "Minimal Downtime", description: "Work around production schedules." },
    ],
  },
  "warehouse-electrical": {
    hero: { pattern: "grid", watermarkIcon: Building2, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Building2, title: "Warehouse Systems", description: "Distribution center electrical infrastructure." },
      { icon: Lightbulb, title: "High Bay Lighting", description: "LED high bay installations for warehouses." },
      { icon: Zap, title: "Power Distribution", description: "Charging stations, dock equipment, and more." },
    ],
  },
  "healthcare-facility-compliance": {
    hero: { pattern: "dots", watermarkIcon: Heart, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Heart, title: "Healthcare", description: "Life-safety electrical for healthcare facilities." },
      { icon: Shield, title: "Compliance", description: "Joint Commission and CMS standards." },
      { icon: Zap, title: "Emergency Power", description: "Generator and transfer switch systems." },
    ],
  },
  "hospital-electrical-contractor": {
    hero: { pattern: "dots", watermarkIcon: Heart, watermarkPosition: "center-right", textAlign: "left" },
    featureCards: [
      { icon: Heart, title: "Hospital Systems", description: "Critical and non-critical electrical systems." },
      { icon: AlertTriangle, title: "Life Safety", description: "Emergency power, nurse call, and fire alarm." },
      { icon: ClipboardList, title: "Inspections", description: "NFPA 99 compliance inspections." },
    ],
  },
  "university-electrical": {
    hero: { pattern: "grid", watermarkIcon: GraduationCap, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: GraduationCap, title: "Campus Electrical", description: "University and college electrical systems." },
      { icon: Building2, title: "Multi-Building", description: "Campus-wide distribution and coordination." },
      { icon: Lightbulb, title: "Energy Efficiency", description: "LED upgrades and building automation." },
    ],
  },
  "school-electrical-contractor": {
    hero: { pattern: "grid", watermarkIcon: GraduationCap, watermarkPosition: "bottom-left", textAlign: "left" },
    featureCards: [
      { icon: GraduationCap, title: "K-12 Schools", description: "Electrical systems for school buildings." },
      { icon: Shield, title: "Student Safety", description: "Arc fault, tamper-resistant outlets, and more." },
      { icon: Lightbulb, title: "LED Upgrades", description: "Classroom and gymnasium lighting upgrades." },
    ],
  },
  "church-electrical-contractor": {
    hero: { pattern: "spotlight", watermarkIcon: Church, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Church, title: "Worship Spaces", description: "Sanctuary, fellowship hall, and classroom electrical." },
      { icon: Lightbulb, title: "Lighting Design", description: "Architectural and stage lighting for worship." },
      { icon: Wifi, title: "AV Wiring", description: "Sound, video, and live streaming infrastructure." },
    ],
  },
  "hotel-electrical-contractor": {
    hero: { pattern: "dots", watermarkIcon: Hotel, watermarkPosition: "center-right", textAlign: "left" },
    featureCards: [
      { icon: Hotel, title: "Hospitality", description: "Hotels, resorts, and conference centers." },
      { icon: Lightbulb, title: "Ambiance", description: "Architectural and accent lighting design." },
      { icon: Cpu, title: "Building Controls", description: "Energy management and guest room controls." },
    ],
  },
  "restaurant-electrical": {
    hero: { pattern: "dots", watermarkIcon: Store, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Store, title: "Retail & Restaurant", description: "Tenant build-outs and renovations." },
      { icon: Lightbulb, title: "Display Lighting", description: "Product lighting and signage power." },
      { icon: Zap, title: "Kitchen Power", description: "High-amperage circuits for commercial kitchens." },
    ],
  },
  "data-center-electrical": {
    hero: { pattern: "circuit", watermarkIcon: Monitor, watermarkPosition: "center-right", textAlign: "left" },
    featureCards: [
      { icon: Monitor, title: "Data Centers", description: "Mission-critical power infrastructure." },
      { icon: Zap, title: "Redundancy", description: "N+1 and 2N power distribution design." },
      { icon: Snowflake, title: "Cooling", description: "Precision cooling for server environments." },
    ],
  },
  "municipal-electrical": {
    hero: { pattern: "grid", watermarkIcon: Landmark, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Landmark, title: "Government", description: "Municipal buildings and public facilities." },
      { icon: Shield, title: "Compliance", description: "ADA, federal, and state code compliance." },
      { icon: ClipboardList, title: "Prevailing Wage", description: "Experienced with government bid processes." },
    ],
  },
  "water-treatment-electrical": {
    hero: { pattern: "waves", watermarkIcon: Factory, watermarkPosition: "bottom-left", textAlign: "left" },
    featureCards: [
      { icon: Factory, title: "Water Treatment", description: "Electrical for water and wastewater facilities." },
      { icon: Cpu, title: "SCADA Systems", description: "Process control and monitoring systems." },
      { icon: Zap, title: "Motor Controls", description: "Pump motor controls and VFD installations." },
    ],
  },
  "food-beverage-manufacturing": {
    hero: { pattern: "circuit", watermarkIcon: Factory, watermarkPosition: "top-right", textAlign: "left" },
    featureCards: [
      { icon: Factory, title: "Food Manufacturing", description: "USDA and FDA compliant electrical installations." },
      { icon: Snowflake, title: "Refrigeration", description: "Cold storage and process cooling electrical." },
      { icon: Shield, title: "Sanitary", description: "Washdown-rated equipment and enclosures." },
    ],
  },
  "stadium-arena-electrical": {
    hero: { pattern: "spotlight", watermarkIcon: Landmark, watermarkPosition: "center-right", textAlign: "center" },
    featureCards: [
      { icon: Landmark, title: "Stadiums & Arenas", description: "Large venue electrical and lighting systems." },
      { icon: Sun, title: "Event Lighting", description: "Broadcast-quality lighting for live events." },
      { icon: Zap, title: "Power Distribution", description: "High-capacity power for concessions and AV." },
    ],
  },
};

// Service area cities for interlinking
const serviceAreaLinks = [
  { label: "Louisville, KY", href: "/service-area/louisville-ky" },
  { label: "Elizabethtown, KY", href: "/service-area/elizabethtown-ky" },
  { label: "Lexington, KY", href: "/service-area/lexington-ky" },
  { label: "New Albany, IN", href: "/service-area/new-albany-in" },
  { label: "Shepherdsville, KY", href: "/service-area/shepherdsville-ky" },
  { label: "Jeffersontown, KY", href: "/service-area/jeffersontown-ky" },
];

const allServices = [...allElectricalServices, ...hvacServices, ...energyServices, ...industryServices, ...stadiumServices];

interface ServicePageProps {
  basePath: string;
  baseLabel: string;
}

// Slug redirects for merged/renamed service pages
export const serviceSlugRedirects: Record<string, string> = {
  "utility-bill-evaluation": "energy-bill-analysis",
  "performance-contracting": "guaranteed-energy-savings",
};

/* Slugs whose intro copy speaks to homeowners — these get the residential variant. */
const residentialSlugs = new Set([
  "residential-hvac",
  "furnace-installation-repair",
  "heat-pump-installation",
  "indoor-air-quality",
  "geothermal-heating-cooling",
]);

const ServicePageTemplate = ({ basePath, baseLabel }: ServicePageProps) => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const headings = useMainHeadings();

  useEffect(() => {
    if (slug && serviceSlugRedirects[slug]) {
      navigate(`${basePath}/${serviceSlugRedirects[slug]}`, { replace: true });
    }
  }, [slug, basePath, navigate]);


  const service = allServices.find((s) => s.slug === slug);

  if (!service) {
    return (
      <Layout>
        <div className="container section-padding text-center">
          <h1 className="font-heading text-3xl font-bold">Service Not Found</h1>
          <Link to="/" className="text-primary mt-4 inline-block">Return Home</Link>
        </div>
      </Layout>
    );
  }

  const breadcrumbs = [{ label: baseLabel, href: basePath }, { label: service.title }];
  const schemas = [
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: baseLabel, url: basePath },
      { name: service.title, url: `${basePath}/${service.slug}` },
    ]),
    faqSchema(service.faqs),
    serviceSchema({
      name: service.title,
      description: service.metaDescription,
      url: `${basePath}/${service.slug}`,
      category: baseLabel,
    }),
  ];

  const visuals = serviceVisuals[service.slug];
  const hasExplicitHeroChoice = service.slug in heroPhotos;
  const heroPhoto = heroPhotos[service.slug] || (hasExplicitHeroChoice ? undefined : heroPhotoFallback[basePath]);
  const heroPhotoAlt = heroPhotos[service.slug]
    ? heroPhotoAlts[service.slug] || `${service.title} by Hadley Energy Solutions in Louisville, KY`
    : heroPhoto
      ? heroPhotoFallbackAlts[basePath] || `${service.title} by Hadley Energy Solutions in Louisville, KY`
      : undefined;
  const isResidential = residentialSlugs.has(service.slug);
  const facts: Fact[] = visuals?.stats?.map((s) => ({ value: s.value, label: s.label })) || defaultFacts;

  return (
    <Layout>
      <PageSEO title={service.metaTitle} description={service.metaDescription} canonical={`${basePath}/${service.slug}`} schemas={schemas} />
      <PageHero
        title={service.h1}
        eyebrow={isResidential ? "FOR HOMEOWNERS" : baseLabel}
        lede={service.intro}
        ctas={[
          { label: isResidential ? "Request Service" : "Request an Estimate", href: "/request-estimate", variant: "primary", dataCta: "hero-estimate" },
          { label: "Call (502) 921-3777", href: "tel:+15029213777", external: true, variant: "outline", phoneIcon: true, dataCta: "hero-call" },
        ]}
        image={heroPhoto}
        imageAlt={heroPhotoAlt}
        breadcrumbs={breadcrumbs}
        heroConfig={visuals?.hero}
      />

      <FactStrip facts={facts} />

      {/* Content */}
      <section className="py-10 md:py-14">
        <div className="container grid gap-10 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-14">
          <div className="svc-prose min-w-0 max-w-[720px]">

          {/* At a glance */}
          {visuals?.featureCards && (
            <AtAGlance items={visuals.featureCards.map((c) => ({ title: c.title, description: c.description }))} />
          )}

          <div className="mt-8">
            <PageJumpSelect headings={headings} />
          </div>

          {/* Callout Box */}
          {visuals?.callout && (
            <div className="mt-8">
              <CalloutBox icon={visuals.callout.icon} title={visuals.callout.title} description={visuals.callout.description} />
            </div>
          )}

            <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">{service.whoNeeds.heading}</h2>
            <p className="text-muted-foreground font-body text-[17px] leading-[1.7] mb-8">{service.whoNeeds.content}</p>

            <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">{service.included.heading}</h2>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mb-8">
              {service.included.items.map((item, i) => (
                <li key={i} className="border-b border-border/60 py-2 text-muted-foreground font-body text-[16px] leading-[1.6]">
                  {item}
                </li>
              ))}
            </ul>

            <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">{service.process.heading}</h2>
            <div className="grid gap-x-10 gap-y-6 sm:grid-cols-2 mb-8">
              {service.process.steps.map((step, i) => (
                <div key={i} className="flex gap-4">
                  <div className="shrink-0 font-condensed text-[40px] font-bold leading-none text-primary">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold">{step.title}</h3>
                    <p className="text-muted-foreground font-body text-[16px] leading-[1.7]">{step.desc}</p>
                  </div>

                </div>
              ))}
            </div>

            <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">{service.costFactors.heading}</h2>
            <p className="text-muted-foreground font-body mb-4">{service.costFactors.content}</p>
            <ul className="grid gap-x-10 gap-y-2 sm:grid-cols-2 mb-8">
              {service.costFactors.factors.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-muted-foreground font-body">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" /> {f}
                </li>
              ))}
            </ul>

          {/* Long-form copy merged in from retired Louisville landing pages */}
          {service.extraSections?.map((sec) => (
            <div key={sec.heading}>
              <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">{sec.heading}</h2>
              <p className="text-muted-foreground font-body text-[17px] leading-[1.7] mb-6">{sec.content}</p>
              {sec.items && (
                <ul className="grid gap-x-10 gap-y-2 sm:grid-cols-2 mb-8">
                  {sec.items.map((it, j) => (
                    <li key={j} className="border-b border-border/60 py-2 text-muted-foreground font-body text-[16px] leading-[1.6]">{it}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* Transparent pricing table (merged from retired Louisville landing pages) */}

          {service.pricing && (
            <div>
              <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">{service.pricing.heading}</h2>
              <p className="text-muted-foreground font-body text-[17px] leading-[1.7] mb-6">{service.pricing.intro}</p>
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-3 pr-4 font-heading text-sm font-bold">Service</th>
                      <th className="py-3 pr-4 font-heading text-sm font-bold whitespace-nowrap">Typical Range</th>
                      <th className="py-3 font-heading text-sm font-bold">What's Included</th>
                    </tr>
                  </thead>
                  <tbody>
                    {service.pricing.rows.map((row, i) => (
                      <tr key={i} className="border-b border-border/60">
                        <td className="py-3 pr-4 font-body text-sm font-semibold">{row.label}</td>
                        <td className="py-3 pr-4 font-body text-sm text-primary font-semibold whitespace-nowrap">{row.range}</td>
                        <td className="py-3 font-body text-sm text-muted-foreground">{row.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground font-body mb-8">
                Ranges are estimates for Louisville, KY and Southern Indiana. Every job gets a free written quote before work begins — call (502) 921-3777.
              </p>
            </div>
          )}

          {/* Louisville neighborhoods served */}
          {service.neighborhoods && (
            <div>
              <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">{service.neighborhoods.heading}</h2>
              <p className="text-muted-foreground font-body text-[17px] leading-[1.7] mb-6">{service.neighborhoods.intro}</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {service.neighborhoods.items.map((n, i) => (
                  <span key={i} className="inline-flex items-center rounded-[4px] border border-border bg-secondary/40 px-3 py-1.5 text-sm font-body text-muted-foreground">
                    {n}
                  </span>
                ))}
              </div>
            </div>
          )}


          {/* FAQs */}
            <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">{service.title} FAQs</h2>
            <div className="mb-8 border-t border-border">
              {service.faqs.map((faq, i) => (
                <div key={i} className="border-b border-border py-5">
                  <h3 className="font-heading text-[18px] font-bold mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground font-body text-[16px] leading-[1.7]">{faq.answer}</p>
                </div>
              ))}
            </div>


          {/* Slug-specific deep content blocks */}
          {service.slug === "ac-installation-repair" && (
            <>
                <div className="mb-8">
                  <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">Air Conditioning Repair Louisville, KY</h2>
                  <div className="space-y-4 text-muted-foreground font-body leading-relaxed">
                    <p>
                      When your air conditioning breaks down during a Louisville summer, you need fast, reliable <strong>air conditioning repair in Louisville, KY</strong>. Hadley Energy Solutions provides same-day AC repair service for homes and businesses throughout Jefferson County — from the Highlands and St. Matthews to Jeffersontown, Okolona, and Prospect. Our licensed HVAC technicians diagnose the problem accurately and fix it right the first time.
                    </p>
                    <p>
                      Louisville's hot, humid summers (averaging 87°F+ in July with 70%+ humidity) put extreme stress on air conditioning systems. The most common <strong>AC repair</strong> calls we handle include:
                    </p>
                    <ul className="space-y-2 ml-4">
                      {[
                        "Refrigerant leaks — your AC blows warm air or runs constantly without cooling",
                        "Compressor failure — the outdoor unit won't start or makes loud grinding noises",
                        "Frozen evaporator coil — ice buildup on the indoor unit from airflow or refrigerant issues",
                        "Capacitor failure — the most common AC repair, causing the system to not start ($150–$300 fix)",
                        "Clogged condensate drain — water leaking from the indoor unit onto floors or ceilings",
                        "Blower motor failure — weak airflow or no air coming from vents ($300–$800 repair)",
                        "Thermostat malfunction — AC cycles on and off erratically or won't respond to settings",
                        "Contactor failure — outdoor unit buzzes but won't turn on ($150–$350 repair)",
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-primary shrink-0 mt-1" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p>
                      Our <strong>air conditioning repair</strong> service includes a full system diagnostic — we don't just fix the symptom. We check refrigerant charge, electrical connections, capacitors, contactors, coil condition, and airflow to identify the root cause. This prevents repeat breakdowns and saves you money long-term. For emergency <strong>AC repair in Louisville</strong>, call <a href="tel:+15029213777" className="text-primary font-semibold hover:underline">(502) 921-3777</a>.
                    </p>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">AC Repair Cost Estimates in Louisville</h2>
                  <div className="space-y-4 text-muted-foreground font-body leading-relaxed">
                    <p>
                      Understanding <strong>AC repair costs in Louisville, KY</strong> helps you budget and avoid overpaying. Here's what Louisville homeowners and businesses typically pay for common air conditioning repairs:
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse mb-4">
                        <thead>
                          <tr className="bg-secondary">
                            <th className="text-left p-3 font-heading font-bold border">Repair Type</th>
                            <th className="text-left p-3 font-heading font-bold border">Cost Range</th>
                            <th className="text-left p-3 font-heading font-bold border">Urgency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ["Capacitor replacement", "$150–$300", "Same-day"],
                            ["Contactor replacement", "$150–$350", "Same-day"],
                            ["Refrigerant recharge (R-410A)", "$200–$600", "1–2 days"],
                            ["Blower motor replacement", "$300–$800", "1–2 days"],
                            ["Evaporator coil replacement", "$800–$2,000", "2–3 days"],
                            ["Compressor replacement", "$1,200–$3,000", "May favor replacement"],
                            ["Condensate drain cleaning", "$100–$250", "Same-day"],
                            ["Thermostat replacement", "$150–$400", "Same-day"],
                            ["Full AC tune-up", "$89–$175", "Scheduled"],
                          ].map(([repair, cost, urgency], i) => (
                            <tr key={i} className={i % 2 === 0 ? "bg-background" : "bg-secondary/50"}>
                              <td className="p-3 border">{repair}</td>
                              <td className="p-3 border font-semibold">{cost}</td>
                              <td className="p-3 border">{urgency}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p>
                      <strong>Repair vs. replacement rule of thumb:</strong> If the repair costs more than 50% of a new system and your AC is over 10 years old, replacement usually makes more financial sense. Modern 16+ SEER2 systems use 30–50% less energy than units made before 2010. Read our <Link to="/guide/air-conditioning-repair-louisville-ky" className="text-primary font-semibold hover:underline">complete air conditioning repair guide</Link> or our <Link to="/blog/ac-cost-louisville-ky" className="text-primary font-semibold hover:underline">breakdown of new AC costs in Louisville</Link>.
                    </p>
                  </div>
                </div>

                <div className="mb-8">
                  <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">Signs Your AC Needs Professional Repair</h2>
                  <div className="space-y-4 text-muted-foreground font-body leading-relaxed">
                    <p>
                      Don't wait until your air conditioning stops completely. These warning signs indicate your AC system needs professional attention:
                    </p>
                    <ul className="space-y-2 ml-4">
                      {[
                        "AC runs constantly but the house never reaches the thermostat setting",
                        "Warm or lukewarm air coming from vents instead of cold air",
                        "Unusual noises — grinding, squealing, banging, or clicking sounds",
                        "Water pooling around the indoor unit or water stains on ceiling",
                        "Musty or burning smell when the AC turns on",
                        "Electric bill suddenly spikes without usage changes",
                        "AC cycles on and off every few minutes (short cycling)",
                        "Ice or frost visible on the refrigerant lines or indoor coil",
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-1" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p>
                      Catching these issues early often means a $150–$400 repair instead of a $1,500+ compressor replacement. Schedule an <Link to="/guide/ac-tune-up-louisville-ky" className="text-primary font-semibold hover:underline">AC tune-up ($89–$175)</Link> before summer to catch problems before they become emergencies. For immediate help, call <a href="tel:+15029213777" className="text-primary font-semibold hover:underline">(502) 921-3777</a> for same-day <strong>air conditioning repair in Louisville, KY</strong>.
                    </p>
                  </div>
                </div>
            </>
          )}

            <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">Why Choose Hadley Energy Solutions?</h2>
            <p className="text-muted-foreground font-body mb-8 leading-relaxed">{service.whyChoose}</p>



          {/* Relevant Blog Posts */}
          {(() => {
            const categoryMap: Record<string, string[]> = {
              "/electrical-services": ["Electrical Safety", "Commercial Electrical", "Residential Electrical"],
              "/hvac-services": ["Energy Management", "Residential Electrical"],
              "/energy-management": ["Energy Management"],
              "/stadium-sports-lighting": ["Commercial Electrical", "Energy Management"],
              "/industries": ["Commercial Electrical", "Energy Management"],
            };
            const matchCategories = categoryMap[basePath] || Object.values(categoryMap).flat();
            // Explicit hub → spoke links take priority over category matching, so each
            // keyword cluster's money page points at its own supporting articles.
            const explicit = (clusterSpokes[`${basePath}/${service.slug}`] || [])
              .map(s => blogPosts.find(p => p.slug === s))
              .filter(Boolean) as typeof blogPosts;
            const relevantPosts = explicit.length
              ? explicit.slice(0, 3)
              : blogPosts.filter(p => matchCategories.includes(p.category)).slice(0, 2);

            if (relevantPosts.length === 0) return null;
            return (
              <div>
                <h2 className="mt-16 mb-4 font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">Related Articles</h2>
                <p className="text-muted-foreground font-body text-[17px] leading-[1.7] mb-6">
                  Learn more about this topic from our expert team.
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {relevantPosts.map(post => (
                    <Link key={post.slug} to={`/blog/${post.slug}`} className="group block border rounded-[4px] overflow-hidden hover:border-primary/30 hover:shadow-lg transition-all duration-300">
                      <div className="aspect-[16/9] bg-secondary overflow-hidden">
                        <img src={blogImages[post.slug]} alt={post.heroImageAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" width="800" height="450" />
                      </div>
                      <div className="p-4">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-heading font-semibold mb-2">
                          <Tag className="w-2.5 h-2.5" /> {post.category}
                        </span>
                        <h3 className="font-heading font-bold text-sm leading-snug group-hover:text-primary transition-colors line-clamp-2 mb-1">{post.title}</h3>
                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><Clock className="w-2.5 h-2.5" /> {post.readTime}</span>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="mb-8">
                  <Link to="/blog" className="text-primary font-heading font-semibold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">
                    <BookOpen className="w-4 h-4" /> View All Blog Articles <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            );

          })()}

          </div>

          {/* Sidebar rail */}
          <aside className="min-w-0 lg:sticky lg:top-[100px] lg:self-start">
            <div className="space-y-8 border-t border-border pt-6 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
              <div className="hidden lg:block">
                <PageJumpNav headings={headings} />
              </div>

              <RailContactCard ctaLabel={isResidential ? "Request Service" : "Request an Estimate"} dataCta="service-rail" />

              {/* Related Services */}
              <div>
                <p className="font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary">Related services</p>
                <ul className="mt-3">
                  {service.relatedServices.map((rs, i) => (
                    <li key={i} className="border-b border-border/70 last:border-0">
                      <Link to={rs.href} className="block py-2 font-body text-sm hover:text-primary">
                        {rs.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Keep exploring — re-housed capability + service-area links */}
      <section className="border-t border-border bg-secondary/30 py-12">
        <div className="container">
          <p className="font-condensed text-[13px] font-bold uppercase tracking-[0.12em] text-primary">Keep exploring</p>

          <div className="mt-8 space-y-12">
            <div>
              <h2 className="font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">Related Services</h2>
              <ul className="mt-3 max-w-[420px]">
                {service.relatedServices.map((rs, i) => (
                  <li key={i} className="border-b border-border/70 last:border-0">
                    <Link to={rs.href} className="block py-2 font-body text-sm hover:text-primary">{rs.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">Explore Our Full Capabilities</h2>
              <p className="mt-2 max-w-[68ch] font-body text-[15px] leading-relaxed text-muted-foreground">
                As a dual-licensed electrical and HVAC contractor, Hadley Energy Solutions offers a complete range of services under one roof.
              </p>
              <ul className="mt-3 max-w-[420px]">
                {[
                  { label: "Electrical Services", href: "/electrical-services" },
                  { label: "HVAC Services", href: "/hvac-services" },
                  { label: "Energy Management", href: "/energy-management" },
                  { label: "Industries We Serve", href: "/industries" },
                  { label: "Stadium & Sports Lighting", href: "/stadium-sports-lighting" },
                  { label: "View Our Portfolio", href: "/portfolio" },
                ]
                  .filter(link => link.href !== basePath)
                  .map((link, i) => (
                    <li key={i} className="border-b border-border/70 last:border-0">
                      <Link to={link.href} className="block py-2 font-body text-sm hover:text-primary">{link.label}</Link>
                    </li>
                  ))}
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">Service Areas</h2>
              <p className="mt-2 max-w-[68ch] font-body text-[15px] leading-relaxed text-muted-foreground">
                We provide {service.title.toLowerCase()} throughout Kentucky and Southern Indiana.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {serviceAreaLinks.map((area, i) => (
                  <Link key={i} to={area.href} className="inline-flex items-center rounded-[4px] border border-border bg-background px-2.5 py-1.5 font-body text-[13px] hover:border-primary hover:text-primary">
                    {area.label}
                  </Link>
                ))}
                <Link to="/service-area" className="inline-flex items-center rounded-[4px] border border-primary/25 bg-primary/10 px-2.5 py-1.5 font-body text-[13px] font-semibold text-primary hover:bg-primary/15">
                  All Service Areas
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>




      {[
        "commercial-electrical-contractor",
        "industrial-electrical-contractor",
        "electrical-service-and-repair",
        "electrical-panel-upgrades",
        "emergency-electrical-service",
        "electrical-design-build",
        "switchgear-installation",
        "lighting-retrofits",
        "lighting-design",
        "lighting-controls",
        "outdoor-commercial-lighting",
        "commercial-hvac",
        "hvac-installation",
        "preventative-maintenance",
        "building-automation-systems",
        "indoor-air-quality",
        "energy-audits",
        "energy-bill-analysis",
        "guaranteed-energy-savings",
        "energy-efficiency-consulting",
      ].includes(service.slug) && (
        <section className="py-10 bg-secondary/40 border-y border-border">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-background border-2 border-primary/30 rounded-[4px] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <BookOpen className="w-7 h-7 text-primary" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold tracking-wider text-primary uppercase mb-2 font-body">Related Pricing Guide</div>
                <h3 className="text-2xl md:text-3xl font-heading font-bold mb-2">Commercial Electrical Costs in Louisville, KY — 2026 Pricing Guide</h3>
                <p className="text-muted-foreground font-body mb-4">Hourly rates, panel-upgrade pricing, service-call fees, ROI benchmarks, and permit requirements for Louisville commercial projects.</p>
                <Link
                  to="/guide/commercial-electrical-costs-louisville-ky"
                  className="inline-flex items-center gap-2 text-primary font-semibold font-body hover:gap-3 transition-all"
                >
                  Read the 2026 Louisville Commercial Electrical Pricing Guide <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      <TestimonialRow />

      <section className="border-t border-border py-14">
        <div className="container max-w-[720px]">
          <h2 className="font-heading text-[30px] font-bold leading-tight tracking-[-0.01em]">
            {isResidential ? "Need service at your home?" : "Ready to Get Started?"}
          </h2>
          <div className="mt-6">
            <ContactForm compact source={`service_${service.slug}`} />
          </div>
        </div>
      </section>


    </Layout>
  );
};

export default ServicePageTemplate;
